import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, MessageCircle, Compass, Anchor, FileCheck } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-36 lg:pb-32 lg:pt-44"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-8">
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-clay"
            data-testid="hero-eyebrow"
          >
            Relocation &amp; Unternehmensberatung · Florida
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-8 font-serif text-5xl leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            data-testid="hero-headline"
          >
            <span className="hero-word">Auswandern</span>{" "}
            <span className="hero-word">nach Florida.</span>
            <br />
            <span className="hero-word">Gründen.</span>{" "}
            <span className="hero-word italic text-clay">Ankommen.</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ink-muted md:text-lg"
            data-testid="hero-subline"
          >
            Komplettbetreuung auf Deutsch – von A bis Z: Unternehmensberatung
            und Firmengründung in Florida, fixfertige Business-Ideen inklusive
            Businessplan, alle Visakategorien (E-2, EB-2 NIW, EB-1C, L-1A)
            samt Antragstellung – und Betreuung auch nach Visumserteilung und
            Auswanderung, bis hin zu Marketing und Kundengewinnung.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-12 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href="/kontakt"
              data-testid="hero-cta-booking"
              className="group inline-flex items-center justify-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
            >
              Kostenloses Erstgespräch buchen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="/leistungen"
              data-testid="hero-cta-services"
              className="inline-flex items-center justify-center gap-3 border border-ink px-8 py-4 text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:border-clay hover:text-clay"
            >
              Leistungen entdecken
              <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>

          <motion.ul
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-16 grid max-w-4xl grid-cols-1 gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4"
            data-testid="hero-trust-chips"
          >
            {[
              { icon: MessageCircle, text: "Betreuung auf Deutsch" },
              { icon: FileCheck, text: "Visa-Antragstellung von A bis Z" },
              { icon: Compass, text: "Aus echter Erfahrung" },
              { icon: Anchor, text: "Vor Ort in Florida" },
            ].map(({ icon: Icon, text }) => (
              <li
                key={text}
                className="flex items-center gap-3 bg-sand px-5 py-4 text-sm text-ink"
              >
                <Icon className="h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} />
                {text}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="relative hidden lg:col-span-4 lg:block"
        >
          <div className="editorial-frame ml-auto w-full max-w-sm">
            <img
              src="https://images.unsplash.com/photo-1623424455593-da762bf80815?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxtaWFtaSUyMHBhbG0lMjB0cmVlcyUyMHdhcm0lMjBzdW5zZXR8ZW58MHx8fHwxNzkyMzYxMzY5fDA&ixlib=rb-4.1.0&q=85"
              alt="Palmen in Florida im warmen Abendlicht – der Traum von der Auswanderung"
              data-testid="hero-image"
              className="aspect-[3/4] w-full object-cover"
              loading="eager"
            />
          </div>
          <p
            className="mt-8 text-right text-xs uppercase tracking-[0.3em] text-ink-muted"
            data-testid="hero-image-caption"
          >
            Florida, USA — Ihr neuer Ankerplatz
          </p>
        </motion.div>
      </div>
    </section>
  );
}
